import React, { Component } from 'react';
import { IDebtLetterHistoryGridProps } from '../../types/PropTypes';
import { IDebtLetterHistoryGridState } from '../../types/stateTypes';
import DriverService from '../../services/driver/driver-service';
import { IDebtLetterHistoryDetails, IDebtLetterHistoryList } from '../../models/driver';
import StandardResponse from '../../models/standard-response';
import { toast } from 'react-toastify';
import Table from '../../components/table/Table';
import moment from 'moment';
import { Common } from '../../utils/constants';
import _ from 'lodash';
import { regionDateAndTime } from '../../utils';
import { getCurrencyFormat, getFormattedCurrencyWithSymbol } from '../../components/common/LocalNumberFormat';
import images from '../../assets/images';

class DebtLetterHistoryGrid extends Component<IDebtLetterHistoryGridProps, IDebtLetterHistoryGridState> {
    private driverService: DriverService;
    constructor(props: IDebtLetterHistoryGridProps) {
        super(props);

        this.state = {
            debtHistoryList: [],
            columns: [],
            currentPage: 1,
            offset: 0,
            rowsPerPage: 10,
            sortColumn: 'creation_date_time',
            sortOrder: 'desc',
            totalCount: 0,
        };
        this.driverService = new DriverService();
    }
    async componentDidMount() {
        const { t } = this.props;
        const header = [
            {
                label: t('driver.label.letterID'),
                id: 'letter_id',
                sortable: true,
                isVisible: true,
                cell: (row: IDebtLetterHistoryDetails) => {
                    return (
                        <span className="d-block text-nowrap-normal" title={row.letter_id}>
                            {row.letter_id}
                        </span>
                    );
                },
            },
            {
                label: t('driver.label.debtValue'),
                id: 'driver_debt_amount',
                sortable: true,
                isVisible: true,
                cell: (row: IDebtLetterHistoryDetails) => {
                    return (
                        <span className="d-block text-nowrap-normal" title={row.driver_debt_amount}>
                            {row.driver_debt_amount
                                ? getFormattedCurrencyWithSymbol(
                                      row.driver_debt_amount?.toString(),
                                      'text',
                                      getCurrencyFormat()?.FINANCIAL_CURRENCY_FORMAT,
                                      row?.currency_details?.symbol,
                                  )
                                : '-'}
                        </span>
                    );
                },
            },
            {
                label: t('driver.label.letterTemplate'),
                id: 'debtor_letter_name',
                sortable: true,
                isVisible: true,
                cell: (row: IDebtLetterHistoryDetails) => {
                    return (
                        <span className="d-block text-nowrap-normal" title={row.debtor_letter_name}>
                            {row.debtor_letter_name}
                        </span>
                    );
                },
            },
            {
                label: t('driver.label.dateGenerated'),
                id: 'creation_date_time',
                sortable: true,
                isVisible: true,
                cell: (row: IDebtLetterHistoryDetails) => {
                    if (row.creation_date_time) {
                        return moment(row.creation_date_time).format(regionDateAndTime().GENERAL_DATE_TIME_FORMAT);
                    }
                    return null;
                },
            },
        ];
        this.setState(
            {
                columns: header,
            },
            () => {
                this.syncSearch().then(async () => {
                    const debtHistoryList = await this.getDriverDebtHistoryList();
                    this.setState({ ...this.state, debtHistoryList });
                });
            },
        );
    }
    syncSearch = (): Promise<boolean> => {
        return new Promise((resolve) => {
            resolve(true);
        });
    };
    getDriverDebtHistoryList = async () => {
        let debtHistoryList: Array<any> = [];

        const filterInput = {
            limit: this.state.rowsPerPage,
            offset: this.state.offset,
            order: [[this.state.sortColumn, this.state.sortOrder]],
        };

        await this.driverService
            .getDriverDebtHistoryListByID(this.props.driverDetails?.id, filterInput)
            .then(async (res: StandardResponse<IDebtLetterHistoryList>) => {
                debtHistoryList = res.Data.rows;
                this.setState({ totalCount: res.Data.count });
            })
            .catch((m) => {
                if (m.response && m.response.data && m.response.data.message) {
                    toast.error(m.response.data.message);
                } else {
                    toast.error(m.toString());
                }
            });

        return debtHistoryList;
    };
    handlePageChange = async (currentPage: number, rowsPerPage: number) => {
        const offset = (currentPage - 1) * rowsPerPage;
        this.setState(
            {
                currentPage: currentPage,
                rowsPerPage: rowsPerPage,
                offset: offset,
            },
            () => {
                this.syncSearch().then(async () => {
                    const debtHistoryList = await this.getDriverDebtHistoryList();
                    this.setState({ ...this.state, debtHistoryList });
                });
            },
        );
    };
    handleSorting = async (columnName: string) => {
        let sortOrder = this.state.sortOrder;
        if (this.state.sortColumn === columnName) {
            sortOrder = sortOrder === 'desc' ? 'asc' : 'desc';
        } else {
            sortOrder = 'asc';
        }

        this.setState(
            {
                sortOrder: sortOrder,
                sortColumn: columnName,
            },
            () => {
                this.syncSearch().then(async () => {
                    const debtHistoryList = await this.getDriverDebtHistoryList();
                    this.setState({ ...this.state, debtHistoryList });
                });
            },
        );
    };

    handleSearch = () => {
        this.setState(
            {
                offset: 0,
                currentPage: 1,
            },
            () => {
                this.syncSearch().then(async () => {
                    const debtHistoryList = await this.getDriverDebtHistoryList();
                    this.setState({ ...this.state, debtHistoryList });
                });
            },
        );
    };

    render() {
        const { t } = this.props;

        return (
            <>
                <div className="title row align-items-content justify-content-between pb-3">
                    <div className="col-auto col-sm text-truncate pr-0">
                        <h1 className="text-truncate">{`${this.props.driverDetails?.full_name} - ${t(
                            'driver.debtLetterHistory',
                        )}`}</h1>
                    </div>
                    <div className="col-auto">
                        <div className="btn-list">
                            <button
                                title={t<string>('driver.button.back')}
                                type="button"
                                className="btn btn-icon pr-0 btn-outline-primary"
                                onClick={() => this.props.toggleClosePage()}
                            >
                                <img className="icon" src={images.backArrowSVG} alt={t<string>('driver.button.back')} />
                            </button>
                        </div>
                    </div>
                </div>
                <div className="card-outer">
                    <Table
                        columns={this.state.columns}
                        data={this.state.debtHistoryList}
                        rowsPerPage={this.state.rowsPerPage}
                        currentPage={this.state.currentPage}
                        totalCount={this.state.totalCount}
                        sortColumn={this.state.sortColumn}
                        sortOrder={this.state.sortOrder}
                        handlePageChange={this.handlePageChange}
                        handleSorting={this.handleSorting}
                        t={this.props.t}
                        isCheckboxGrid={false}
                    />
                </div>
            </>
        );
    }
}
export default DebtLetterHistoryGrid;
